import { useParams } from '../../../contexts/RouterContext';
import { useCurrentProject } from '../../../hooks/api/projects/useCurrentProject';
import { useReleaseGroup } from '../../../hooks/api/releaseGroups/useReleaseGroup';
import { toFiniteNumber } from '../../../utils';
import { isReleaseGroupScope } from '../types';
import type { ReportsV3Params, ReportType } from '../types';

/**
 * Returns the default saved option ID for the current scope (project or release group)
 * and report type. Returns null if no default is configured, the data hasn't loaded yet,
 * or the report type doesn't support defaults (vulnerability, remediationGuidance).
 */
export const useDefaultSavedOptionId = (reportType: ReportType): number | null => {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);

  // Always call both hooks (Rules of Hooks) — only one will return meaningful data.
  const { data: project } = useCurrentProject(!isReleaseGroup);
  const { data: releaseGroup } = useReleaseGroup(
    isReleaseGroup ? toFiniteNumber(params.projectGroupId) ?? 0 : 0,
    isReleaseGroup,
  );

  if (reportType !== 'licensing' && reportType !== 'sbom') {
    return null;
  }

  const field = reportType === 'licensing'
    ? 'defaultSavedOptionLicensingReport'
    : 'defaultSavedOptionSbom';

  if (isReleaseGroup) {
    return releaseGroup?.[field] ?? null;
  }

  return project?.[field] ?? null;
};
